import { apiGet, apiPost } from "./apiUtils/api";
import { GET_USER_PROFILE, GET_USER_PROFILES } from "./apiUtils/apiUrls";

export const normalizeUserProfile = (user) => {
  if (!user || typeof user !== "object") {
    return user;
  }

  const normalizedMobile =
    user.mobileNumber ||
    user.mobilenumber ||
    user.phoneNumber ||
    user.phone_number ||
    user.telephoneNumber ||
    user.telephonenumber ||
    null;

  const normalizedTelephone =
    user.telephoneNumber ||
    user.telephonenumber ||
    user.phoneNumber ||
    user.phone_number ||
    normalizedMobile ||
    null;

  const normalizeRoleId = (roleId) => {
    if (typeof roleId !== "number") return roleId ?? null;
    if (roleId === 0) {
      return 1;
    }
    return roleId;
  };

  const normalizedRoles = Array.isArray(user.roles)
    ? user.roles.map((role) => {
        const originalRole = role?.role ?? role?.roleId;
        const resolvedRole = normalizeRoleId(originalRole);
        return {
          ...role,
          rawRole: typeof originalRole === "number" ? originalRole : resolvedRole,
          role: resolvedRole,
          roleId: resolvedRole ?? role?.roleId,
          userCompanyId: role?.userCompanyId ?? role?.companyId ?? role?.id,
        };
      })
    : [];

  return {
    ...user,
    id: user?.id ?? user?.userId ?? user?.user_Id ?? user?.userID ?? null,
    userId: user?.userId ?? user?.user_Id ?? user?.id ?? user?.userID ?? null,
    user_Id: user?.user_Id ?? user?.userId ?? user?.id ?? user?.userID ?? null,
    mobileNumber: normalizedMobile,
    telephoneNumber: normalizedTelephone,
    phoneNumber: user?.phoneNumber ?? normalizedMobile,
    roles: normalizedRoles,
  };
};

let cachedUserProfile = null;
let userProfilePromise = null;

export const setCachedUserProfile = (profile) => {
  cachedUserProfile = profile ? normalizeUserProfile(profile) : null;
};

export const clearCachedUserProfile = () => {
  cachedUserProfile = null;
  userProfilePromise = null;
};

export const getCachedUserProfile = () => cachedUserProfile;

export const fetchUserProfileOnce = async ({ forceRefresh = false } = {}) => {
  if (!forceRefresh && cachedUserProfile) {
    return cachedUserProfile;
  }
  if (!forceRefresh && userProfilePromise) {
    return userProfilePromise;
  }

  userProfilePromise = apiGet(GET_USER_PROFILE)
    .then((response) => {
      if (response?.isSuccess) {
        const normalized = normalizeUserProfile(response.result);
        cachedUserProfile = normalized;
        return normalized;
      }
      throw new Error("Failed to fetch user profile");
    })
    .finally(() => {
      userProfilePromise = null;
    });

  return userProfilePromise;
};

const FALLBACK_COMPANY_ID = 48;

const companyIdForProfileLookup = () => {
  try {
    const stored = sessionStorage.getItem("selectedAdminCompanyId");
    const parsed = Number(stored);
    if (stored && Number.isFinite(parsed) && parsed > 0) return parsed;
  } catch {
  }
  return FALLBACK_COMPANY_ID;
};

export const fetchUserProfilesByEmail = async (email) => {
  if (!email) return [];
  try {
    const response = await apiPost(
      GET_USER_PROFILES,
      { email },
      { headers: { "x-company-id": companyIdForProfileLookup() } }
    );
    if (response?.isSuccess && Array.isArray(response.result)) {
      return response.result.map(normalizeUserProfile);
    }
    return [];
  } catch {
    return [];
  }
};

const mergeProfilesForEmail = (profiles) => {
  const roles = profiles.flatMap((profile) =>
    (profile.roles || []).map((role) => ({ ...role, userId: profile.userId }))
  );
  const highest = resolvePrimaryRole(roles);
  const primary =
    profiles.find((profile) => profile.userId === highest?.userId) || profiles[0];
  const profilesByUserId = Object.fromEntries(
    profiles.map((profile) => [profile.userId, profile])
  );
  return { ...primary, roles, profilesByUserId };
};

const ROLE_PRIORITY = [0, 1, 2, 3];

const rankOf = (entry) => (typeof entry?.rawRole === "number" ? entry.rawRole : entry?.role);

const numericUserId = (entry) => {
  const raw = entry?.userId ?? entry?.user_Id ?? entry?.id;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const highestIdAmong = (entries) => {
  if (entries.length <= 1) return entries[0] ?? null;
  let best = entries[0];
  let bestId = numericUserId(best);
  for (const entry of entries.slice(1)) {
    const id = numericUserId(entry);
    if (id !== null && (bestId === null || id > bestId)) {
      best = entry;
      bestId = id;
    }
  }
  return best;
};

export const resolvePrimaryRole = (roles) => {
  if (!Array.isArray(roles) || roles.length === 0) return null;
  for (const wanted of ROLE_PRIORITY) {
    const matches = roles.filter((entry) => rankOf(entry) === wanted);
    if (matches.length > 0) return highestIdAmong(matches);
  }
  return roles[0] ?? null;
};

export const resolveRoleForDestination = (roles, destinationPath, wantedTransactionId) => {
  if (!Array.isArray(roles) || roles.length === 0) return null;
  const path = String(destinationPath || "");

  if (path.startsWith("/employee")) {
    const employeeRoles = roles.filter((entry) => entry?.role === 3);
    if (employeeRoles.length === 0) return resolvePrimaryRole(roles);
    if (wantedTransactionId != null && wantedTransactionId !== "") {
      const exact = employeeRoles.find(
        (entry) => String(entry?.transactionId) === String(wantedTransactionId)
      );
      if (exact) return exact;
    }
    return employeeRoles[0];
  }

  const adminRoles = roles.filter((entry) => entry?.role === 1 || entry?.role === 2);
  if (adminRoles.length > 0) return resolvePrimaryRole(adminRoles);
  return resolvePrimaryRole(roles);
};

export const resolveAuthorisedUser = async ({ email, forceRefresh = false } = {}) => {
  if (email) {
    const profiles = await fetchUserProfilesByEmail(email);
    if (profiles.length > 0) {
      const merged = mergeProfilesForEmail(profiles);
      if (merged.roles?.length > 0) {
        return { profile: merged };
      }
    }
  }

  const profile = await fetchUserProfileOnce({ forceRefresh });
  return { profile };
};

